import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Select, MenuItem, FormControl, Button } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axiosInstance from '../../axiosInstance';
import StreakWidget from './StreakWidget';
import ActivityHeatmap from './ActivityHeatmap';
import AnnualGoals from './AnnualGoals';
import RecapCard from './RecapCard';

export default function ActivityTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [stats, setStats] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [goals, setGoals] = useState([]);
  const [recap, setRecap] = useState(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recapLoading, setRecapLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, heatmapRes, goalsRes] = await Promise.all([
        axiosInstance.get('/users/me/streak'),
        axiosInstance.get(`/users/me/streak/heatmap?year=${year}`),
        axiosInstance.get(`/users/me/goals?year=${year}`),
      ]);
      setStats(statsRes.data);
      setHeatmap(heatmapRes.data);
      setGoals(goalsRes.data);
    } catch (err) {
      console.error('ActivityTab load error:', err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleViewRecap = async () => {
    setRecapLoading(true);
    try {
      const res = await axiosInstance.get(`/users/me/recap?year=${year}`);
      setRecap(res.data);
      setRecapOpen(true);
    } catch (err) {
      console.error('Recap load error:', err);
    } finally {
      setRecapLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) yearOptions.push(y);

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Collecting Activity</Typography>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <StreakWidget stats={stats} heatmapData={heatmap} onRefresh={load} />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{year} Activity</Typography>
      <ActivityHeatmap data={heatmap} year={year} />

      <Box sx={{ mt: 3 }}>
        <AnnualGoals goals={goals} year={year} onRefresh={load} />
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          startIcon={recapLoading ? <CircularProgress size={16} /> : <CalendarMonthIcon />}
          onClick={handleViewRecap}
          variant="outlined"
          size="small"
          disabled={recapLoading}
        >
          View {year} Year in Review
        </Button>
      </Box>

      <RecapCard open={recapOpen} onClose={() => setRecapOpen(false)} recap={recap} year={year} />
    </Box>
  );
}
